import React, { useCallback } from 'react'
import { Channel as StreamChannel, StreamChat } from 'stream-chat'
import type { LocalMessage, Message, SendMessageOptions } from 'stream-chat'
import {
  Chat,
  Channel,
  MessageInput,
  MessageList,
  Thread,
  Window,
  useChannelActionContext,
  MessageInputProps,
} from 'stream-chat-react'
import { t } from 'ttag'
import Loader from '../Loader/Loader'
import YumEmptyState from '../YumEmptyState/YumEmptyState'
import i18nInstance from '../../views/ChallengeChat/i18n/i18nInstance'
import { EmptyStateIndicator } from '../../views/ChallengeChat/components/EmptyStateIndicator'
import { CustomModalGallery } from '../../views/ChallengeChat/components/CustomModalGallery'
import { customReactionOptions, CustomReactionSelector, CustomReactionsList } from './reactionOptions'
import { YumCustomMessageActionsList } from './YumMessageActions'
import { YumSystemMessage } from './YumSystemMessage'

import { ReactComponent as ChatEmptyStateIcon } from '../../assets/v3/icons/chat-empty-state.svg'

import './YumChatView.scss'

export interface IYumChatViewProps {
  chatClient?: StreamChat
  currentChannel?: StreamChannel
  error?: boolean
  // Rendered inside the chat window above the message list.
  header?: React.ReactNode
  className?: string
}

const ChannelWindow = ({ header }: { header?: React.ReactNode }) => {
  const { sendMessage } = useChannelActionContext()

  const overrideSubmitHandler: MessageInputProps['overrideSubmitHandler'] = useCallback(
    async ({
      localMessage,
      message,
      sendOptions,
    }: {
      cid: string
      localMessage: LocalMessage
      message: Message
      sendOptions: SendMessageOptions
    }) => {
      // Focus on input to keep showing the keyboard
      const el = document.getElementsByClassName('str-chat__textarea__textarea')
      if (el && el[0]) {
        // @ts-ignore
        el[0].focus()
      }
      await sendMessage({ localMessage, message, options: sendOptions })
    },
    [sendMessage],
  )

  return (
    <>
      <Window>
        {/* stream-chat-react bundles its own @types/react, so its ReactNode type conflicts with the app's */}
        {header as any}
        <MessageList disableQuotedMessages hideDeletedMessages closeReactionSelectorOnClick onlySenderCanEdit />
        <MessageInput overrideSubmitHandler={overrideSubmitHandler} />
      </Window>
      <Thread />
    </>
  )
}

export default function YumChatView(props: IYumChatViewProps) {
  const { chatClient, currentChannel, error, header, className } = props

  return (
    <div className={'yum-chat-view' + (className ? ` ${className}` : '')}>
      {error ? (
        <YumEmptyState text={t`Could not open this chat...`} Icon={ChatEmptyStateIcon} />
      ) : !chatClient || !currentChannel ? (
        <Loader top={true} />
      ) : (
        <Chat client={chatClient} i18nInstance={i18nInstance}>
          <Channel
            channel={currentChannel}
            reactionOptions={customReactionOptions}
            /* @ts-ignore */
            ReactionSelector={CustomReactionSelector}
            ReactionsList={CustomReactionsList}
            EmptyStateIndicator={EmptyStateIndicator}
            ModalGallery={CustomModalGallery}
            CustomMessageActionsList={YumCustomMessageActionsList}
            MessageSystem={YumSystemMessage}
          >
            <ChannelWindow header={header} />
          </Channel>
        </Chat>
      )}
    </div>
  )
}
